/**
 * Payment instruction status - lifecycle of a payment instruction
 */
export enum PaymentInstructionStatus {
  /** RTP/PIS: Waiting for execution to complete */
  PENDING_EXECUTION = 'pending_execution',
  /** Worker has claimed this instruction and is actively dispatching it */
  EXECUTING = 'executing',
  /** Reissuing the payment link for a failed or expired instruction */
  REISSUING = 'reissuing',
  /** Execution sent, waiting for funds to arrive */
  AWAITING_FUNDS = 'awaiting_funds',
  /** Funds are in transit — provider accepted (AC*), bank statement not yet confirmed */
  FUNDS_IN_TRANSIT = 'funds_in_transit',
  /** Some settled funds received, but not the full instruction amount */
  PARTIALLY_COMPLETED = 'partially_completed',
  /** Fully settled — full instruction amount confirmed in bank statement */
  COMPLETED = 'completed',
  /** A payin under this instruction needs manual review — auto-allocation could not proceed */
  NEEDS_REVIEW = 'needs_review',
  /** Execution or payment failed (technical error / dispatch failure) */
  FAILED = 'failed',
  /** Bank explicitly rejected the payment (RJCT webhook) */
  REJECTED = 'rejected',
  /** Cancelled by user */
  CANCELLED = 'cancelled',
  /** Expired due to timeout */
  EXPIRED = 'expired'
}

/**
 * Statuses a payment instruction can still receive settled/in-transit funds under —
 * shared by the domain entity's canReceiveAllocation() check and by candidate-listing
 * queries (e.g. ops-web bank-credit resolution) so both stay in sync.
 */
export const RECEIVABLE_PAYMENT_INSTRUCTION_STATUSES: readonly PaymentInstructionStatus[] = [
  PaymentInstructionStatus.AWAITING_FUNDS,
  PaymentInstructionStatus.FUNDS_IN_TRANSIT,
  PaymentInstructionStatus.PARTIALLY_COMPLETED
];

/** Statuses that mean the buyer's money arrived — paid in full, in part, or on its way. */
export const PAID_PAYMENT_INSTRUCTION_STATUSES: readonly PaymentInstructionStatus[] = [
  PaymentInstructionStatus.FUNDS_IN_TRANSIT,
  PaymentInstructionStatus.PARTIALLY_COMPLETED,
  PaymentInstructionStatus.COMPLETED
];

/** Instructions the AI may propose for a credit: nothing in flight from a provider, still owing. */
export const CREDIT_MATCH_CANDIDATE_STATUSES: readonly PaymentInstructionStatus[] = [
  PaymentInstructionStatus.AWAITING_FUNDS,
  PaymentInstructionStatus.PARTIALLY_COMPLETED
];

/**
 * Statuses that can no longer receive a bank-statement match — the instruction's
 * lifecycle is over, one way or another. Shared by statement-matching candidate
 * queries and by the buyer-deletion "no active instruction" gate, so both agree on
 * what "still active" means.
 */
export const NON_MATCHABLE_INSTRUCTION_STATUSES: readonly PaymentInstructionStatus[] = [
  PaymentInstructionStatus.COMPLETED,
  PaymentInstructionStatus.FAILED,
  PaymentInstructionStatus.REJECTED,
  PaymentInstructionStatus.CANCELLED,
  PaymentInstructionStatus.EXPIRED
];

/**
 * Terminal failure/cancellation — the instruction's money will never arrive. The
 * non-matchable set minus COMPLETED (which is terminal but did receive its money).
 */
export const TERMINAL_PAYMENT_INSTRUCTION_STATUSES: readonly PaymentInstructionStatus[] =
  NON_MATCHABLE_INSTRUCTION_STATUSES.filter(
    status => status !== PaymentInstructionStatus.COMPLETED
  );

/**
 * Statuses whose amount counts against a purchase's collection cap — the complement of
 * terminal. Derived, not enumerated: a newly added status counts toward the cap until
 * someone deliberately marks it terminal, which is the safe direction to drift.
 */
export const CAP_COUNTING_PAYMENT_INSTRUCTION_STATUSES: readonly PaymentInstructionStatus[] =
  Object.values(PaymentInstructionStatus).filter(
    status => !TERMINAL_PAYMENT_INSTRUCTION_STATUSES.includes(status)
  );

/**
 * Curated display grouping for instruction lists (e.g. the trust account Instructions tab) —
 * collapses the 12 raw statuses into 3 buyer/lawyer-facing groups. A `Record` so the compiler
 * fails when a new status is added without a group.
 */
export enum PaymentInstructionStatusGroup {
  IN_PROGRESS = 'in_progress',
  NEEDS_ATTENTION = 'needs_attention',
  COMPLETED = 'completed'
}

export const PAYMENT_INSTRUCTION_STATUS_GROUP: Record<
  PaymentInstructionStatus,
  PaymentInstructionStatusGroup
> = {
  [PaymentInstructionStatus.PENDING_EXECUTION]: PaymentInstructionStatusGroup.IN_PROGRESS,
  [PaymentInstructionStatus.EXECUTING]: PaymentInstructionStatusGroup.IN_PROGRESS,
  [PaymentInstructionStatus.REISSUING]: PaymentInstructionStatusGroup.IN_PROGRESS,
  [PaymentInstructionStatus.AWAITING_FUNDS]: PaymentInstructionStatusGroup.IN_PROGRESS,
  [PaymentInstructionStatus.FUNDS_IN_TRANSIT]: PaymentInstructionStatusGroup.IN_PROGRESS,
  [PaymentInstructionStatus.PARTIALLY_COMPLETED]: PaymentInstructionStatusGroup.IN_PROGRESS,
  [PaymentInstructionStatus.NEEDS_REVIEW]: PaymentInstructionStatusGroup.IN_PROGRESS,
  [PaymentInstructionStatus.FAILED]: PaymentInstructionStatusGroup.NEEDS_ATTENTION,
  [PaymentInstructionStatus.REJECTED]: PaymentInstructionStatusGroup.NEEDS_ATTENTION,
  [PaymentInstructionStatus.EXPIRED]: PaymentInstructionStatusGroup.NEEDS_ATTENTION,
  [PaymentInstructionStatus.COMPLETED]: PaymentInstructionStatusGroup.COMPLETED,
  [PaymentInstructionStatus.CANCELLED]: PaymentInstructionStatusGroup.COMPLETED
};

/** Statuses in the needs-attention group — the only ones that can carry a handled marker. */
export const NEEDS_ATTENTION_PAYMENT_INSTRUCTION_STATUSES: readonly PaymentInstructionStatus[] = (
  Object.keys(PAYMENT_INSTRUCTION_STATUS_GROUP) as PaymentInstructionStatus[]
).filter(
  status =>
    PAYMENT_INSTRUCTION_STATUS_GROUP[status] === PaymentInstructionStatusGroup.NEEDS_ATTENTION
);

export function isNeedsAttentionPaymentInstructionStatus(
  status: PaymentInstructionStatus
): boolean {
  return PAYMENT_INSTRUCTION_STATUS_GROUP[status] === PaymentInstructionStatusGroup.NEEDS_ATTENTION;
}
